/**
 * Internal comment thread for a ticket. Loads existing comments, appends new
 * ones (including those arriving over the realtime channel), and posts a new
 * comment. Comments are team-internal — never shown to the end user.
 *
 * Visual: the Claude Design `.side-sec` "Internal thread" block — avatar +
 * author/time head, muted comment text, and a compose box with a "Team only"
 * segment and a primary Comment button.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import type { Comment } from '@hotline/contracts';

import { useAuth } from '../auth/AuthContext.js';
import { useOrgScope } from '../org/OrgScopeContext.js';
import { useTicketEvents } from '../realtime/useRealtime.js';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Spinner,
  Textarea,
} from './ui/index.js';
import { Icon } from './ui/icons.js';
import { useAsync, toMessage } from '../lib/useAsync.js';

/** Up-to-two-letter initials from a display name / email. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/[\s@.]+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return ((first[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function CommentThread({ sessionId }: { sessionId: string }): ReactElement {
  const { client } = useAuth();
  const { query } = useOrgScope();
  const comments = useAsync<Comment[]>(
    () => client.api.listComments(sessionId, query),
    [client, sessionId, query.orgId],
  );
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  // Live: append a comment created elsewhere for this ticket (dedupe by id).
  useTicketEvents(sessionId, {
    onComment: (payload) => {
      comments.setData((prev) => {
        const list = prev ?? [];
        if (list.some((c) => c.id === payload.comment.id)) return list;
        return [...list, payload.comment];
      });
    },
  });

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    setPosting(true);
    setError(null);
    try {
      const created = await client.api.createComment(
        sessionId,
        { body: trimmed, mentions: [] },
        query,
      );
      comments.setData((prev) => {
        const list = prev ?? [];
        if (list.some((c) => c.id === created.id)) return list;
        return [...list, created];
      });
      setBody('');
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setPosting(false);
    }
  };

  const list = comments.data ?? [];

  return (
    <Card>
      <Card.Body className="flex flex-col gap-3.5">
        <h2 className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-faint">
          Internal comments
        </h2>

        {comments.loading ? <Spinner /> : null}
        {comments.error ? <Alert tone="danger">{comments.error}</Alert> : null}

        <ul className="flex flex-col gap-3.5">
          {list.map((c) => {
            const author = c.authorName || c.authorEmail || c.authorUserId;
            return (
              <li
                key={c.id}
                data-comment-id={c.id}
                className="flex gap-2.5"
              >
                <Avatar size="sm" fallback={initialsOf(author)} />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-[7px]">
                    <span className="text-[12.5px] font-bold text-text">
                      {author}
                    </span>
                    <time
                      dateTime={c.createdAt}
                      className="ml-auto text-[11px] text-faint"
                    >
                      {new Date(c.createdAt).toLocaleString()}
                    </time>
                  </div>
                  <p className="text-[12.5px] leading-[1.5] text-text">
                    {c.body}
                  </p>
                </div>
              </li>
            );
          })}
          {comments.data && comments.data.length === 0 ? (
            <li className="text-[12.5px] text-faint">No comments yet.</li>
          ) : null}
        </ul>

        <form
          className="flex flex-col gap-[9px]"
          onSubmit={submit}
          aria-label="Add comment"
        >
          <Textarea
            aria-label="Comment body"
            placeholder="Add an internal note… use @ to mention"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="min-h-[60px] text-[12.5px]"
          />
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <div className="flex items-center gap-[9px]">
            <span className="mr-auto inline-flex h-[21px] items-center gap-[5px] rounded-sm bg-surface-3 px-2 text-[11.5px] font-bold text-muted [&_svg]:size-3">
              <Icon.lock />
              Team only
            </span>
            <Button
              type="submit"
              size="sm"
              disabled={posting || !body.trim()}
            >
              {posting ? 'Posting…' : 'Comment'}
            </Button>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
}
